import { z } from "zod";
import { fileEntrySchema, containerSchema } from "./container.js";
import { itemSchema, containerItemSchema, barcodeLookupResponseSchema } from "./item.js";
export const searchQuerySchema = z.object({
    q: z.string().trim().min(1, "q is required").max(200),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const containerSearchResultSchema = z.object({
    type: z.literal("container"),
    container: containerSchema,
    path: z.string(),
    score: z.number().nonnegative(),
});
export const itemSearchResultSchema = z.object({
    type: z.literal("item"),
    item: itemSchema,
    containerId: z.string(),
    containerName: z.string(),
    path: z.string(),
    score: z.number().nonnegative(),
});
export const searchResultSchema = z.discriminatedUnion("type", [
    containerSearchResultSchema,
    itemSearchResultSchema,
]);
export const searchResponseSchema = z.object({
    results: z.array(searchResultSchema),
});
// ── Item scanning + mirror (item-scan-and-search) ────────────────
/**
 * External barcode-lookup preview, as surfaced to a scan dialog (item not ours
 * yet). Same payload as `GET /api/items/lookup/:barcode`, so the item module
 * owns the shape and this keeps the scan-facing name.
 */
export const barcodePreviewSchema = barcodeLookupResponseSchema;
/**
 * Container-scoped barcode lookup (GET /api/containers/:id/items/lookup/:barcode):
 * whether the barcode resolves to an item the caller already owns (`known`) and,
 * if so, whether & how many sit in this container (`placementHere`). `known` is
 * null when the barcode is new; `external` carries the provider preview only
 * then — a known item never consults a provider again.
 */
export const containerItemLookupResponseSchema = z.object({
    known: itemSchema.nullable().default(null),
    placementHere: containerItemSchema.nullable().default(null),
    external: barcodePreviewSchema.nullable().default(null),
});
/**
 * Local item-mirror sync (GET /api/items/sync, ticket 07): the full accessible
 * item universe plus every placement link, access-filtered for the caller.
 */
export const itemsSyncResponseSchema = z.object({
    items: z.array(itemSchema),
    placements: z.array(containerItemSchema),
});
/**
 * A placement link enriched with the container's display name and full path —
 * the item-detail container list shape (ticket 05).
 */
export const itemContainerSchema = containerItemSchema.extend({
    name: z.string().min(1),
    path: z.string(),
});
/**
 * One photo of the gallery: either a container file photo or an item photo.
 * The two groups of fields are mutually exclusive — container photos have
 * containerId/containerName/containerPath/file, item photos have
 * itemId/itemName/itemPhotoType/photoUrl/photoId.
 */
export const photoResponseSchema = z.object({
    // Container photo fields (mutually exclusive with item fields)
    containerId: z.string().optional(),
    containerName: z.string().optional(),
    containerPath: z.string().optional(),
    file: fileEntrySchema.optional(),
    // Item photo fields
    itemId: z.string().optional(),
    itemName: z.string().optional(),
    itemPhotoType: z.enum(["general", "nutrition"]).optional(),
    photoUrl: z.string().optional(),
    photoId: z.string().optional(),
});
/** GET /photos — the full gallery or the filter-aware subset (ticket 22). */
export const photosResponseSchema = z.object({
    photos: z.array(photoResponseSchema),
});
// ── Image search (server API for non-PWA consumers, ticket 05) ──────
/**
 * POST /api/search/images — a free-text query the server embeds with UForm's
 * text encoder and ranks against stored image embeddings. The PWA itself
 * searches in-browser and does not call this endpoint.
 */
export const imageSearchQuerySchema = z.object({
    q: z.string().trim().min(1, "q is required").max(200),
    limit: z.number().int().min(1).max(100).default(20),
});
/** POST /api/search/images — ranked photos, same `PhotoResponse` tiles as `/api/photos`. */
export const imageSearchResponseSchema = z.object({
    results: z.array(photoResponseSchema),
});
//# sourceMappingURL=search.js.map